import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight, Quote } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal, Overline } from "@/components/Reveal";
import Manifesto from "@/components/Manifesto";
import WhyChoose from "@/components/WhyChoose";
import { MISSION, VISION, CUSTOMERS, USPS, SPACES, OBJECTS, TEAM, VALUES } from "@/data/content";

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        overline="About Us"
        title={<>Beyond Products, <span className="text-gold-gradient italic">Beyond Limitations.</span></>}
        subtitle="At Vernika Designs, we believe every home should reflect the people who live in it. Our team blends creative vision with skilled craftsmanship — bringing warmth, elegance and personality to every corner, from concept to completion."
        image="/photos/brand/about.jpeg"
      />

      {/* Story */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid lg:grid-cols-2 gap-16">
          <Reveal>
            <p className="font-serif-lux text-2xl md:text-3xl leading-snug text-[#e8e8e8]">
              A premium interior solutions company founded to redefine interior design for modern Indian homes — where creative vision meets skilled craftsmanship, and every corner is designed to inspire.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-[#b8b8b8] leading-relaxed">
              VERNIKA DESIGNS LLP brings together premium interior products and complete design-and-build solutions under one trusted roof — from plywood, laminates and hardware to modular kitchens, wardrobes, bathroom accessories and full-home turnkey execution.
            </p>
            <p className="text-[#b8b8b8] leading-relaxed mt-5">
              Whether it&apos;s a cozy pooja corner, a modular kitchen, or an entire home transformation, we approach every project with the same attention to detail and passion for excellence — from the very first conversation to final handover.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-6 border-t border-white/10 pt-8">
              {[["3+","Years of Craft"],["1,000+","Spaces Transformed"],["West Bengal","Serving Across"]].map(([n,l]) => (
                <div key={l}>
                  <div className="font-serif-lux text-3xl md:text-4xl text-gold-gradient">{n}</div>
                  <div className="text-xs uppercase tracking-wider text-[#b8b8b8] mt-1">{l}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="pb-8">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid md:grid-cols-2 gap-px bg-white/5 border border-white/5">
          <Reveal className="bg-[#0a0a0a] p-10 md:p-12">
            <Overline>Our Mission</Overline>
            <p className="mt-5 font-serif-lux text-xl md:text-2xl leading-relaxed text-[#e8e8e8]">{MISSION}</p>
          </Reveal>
          <Reveal delay={0.1} className="bg-[#0a0a0a] p-10 md:p-12">
            <Overline>Our Vision</Overline>
            <p className="mt-5 font-serif-lux text-xl md:text-2xl leading-relaxed text-[#e8e8e8]">{VISION}</p>
          </Reveal>
        </div>
      </section>

      {/* Values */}
      <section data-testid="values-section" className="py-20 md:py-28">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <Reveal className="max-w-2xl">
            <Overline>Our Values</Overline>
            <h2 className="font-serif-lux mt-5 text-4xl md:text-5xl leading-tight tracking-tight">
              What we <span className="text-gold-gradient italic">stand for.</span>
            </h2>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-2 gap-px bg-white/5 border border-white/5">
            {VALUES.map((v, i) => {
              const Icon = Icons[v.icon] || Icons.Sparkles;
              return (
                <Reveal key={v.title} delay={i * 0.05}>
                  <div className="group h-full bg-[#0a0a0a] p-10 md:p-12 hover:bg-[#0e0e0e] transition-colors duration-500" data-testid={`value-${i}`}>
                    <Icon size={30} strokeWidth={1.2} className="text-[#e7c15a] transition-transform duration-500 group-hover:-translate-y-1" />
                    <h3 className="mt-7 font-serif-lux text-3xl text-[#fafafa]">{v.title}</h3>
                    <p className="mt-4 text-[#b8b8b8] leading-relaxed">{v.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* How We Work — USPs */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <Reveal className="max-w-2xl">
            <Overline>How We Work</Overline>
            <h2 className="font-serif-lux mt-5 text-4xl md:text-5xl leading-tight tracking-tight">
              Craft you can <span className="text-gold-gradient italic">see</span>, service you can <span className="text-gold-gradient italic">trust.</span>
            </h2>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-3 gap-px bg-white/5 border border-white/5">
            {USPS.map((u, i) => {
              const Icon = Icons[u.icon] || Icons.Sparkles;
              return (
                <Reveal key={u.title} delay={i * 0.05}>
                  <div className="group h-full bg-[#0a0a0a] p-8 md:p-10 hover:bg-[#0e0e0e] transition-colors duration-500" data-testid={`usp-${i}`}>
                    <div className="flex items-center gap-4">
                      <span className="grid place-items-center h-12 w-12 border border-[#e7c15a]/40 text-[#e7c15a] group-hover:bg-[#e7c15a] group-hover:text-[#050505] transition-colors">
                        <Icon size={20} strokeWidth={1.4} />
                      </span>
                      <span className="font-serif-lux text-3xl text-[#e7c15a]/40">{`0${i + 1}`}</span>
                    </div>
                    <h3 className="mt-6 font-serif-lux text-2xl text-[#fafafa]">{u.title}</h3>
                    <p className="mt-3 text-[#b8b8b8] leading-relaxed text-sm">{u.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <Manifesto />

      {/* Founders / Leadership */}
      <section data-testid="founders-section" className="py-20 md:py-28 bg-[#080808] border-y border-white/5">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <Reveal className="max-w-2xl">
            <Overline>Leadership</Overline>
            <h2 className="font-serif-lux mt-5 text-4xl md:text-5xl leading-tight tracking-tight">
              Meet the founders behind <span className="text-gold-gradient italic">Vernika.</span>
            </h2>
            <p className="mt-5 text-[#b8b8b8] leading-relaxed">
              A partnership grounded in craft, commerce and a shared belief that interiors should reflect the people who live and work in them.
            </p>
          </Reveal>

          <div className="mt-14 grid lg:grid-cols-2 gap-6 md:gap-8">
            {TEAM.map((m, i) => (
              <Reveal key={m.name} delay={i * 0.08}>
                <article data-testid={`founder-${i}`} className="relative h-full border border-white/10 bg-[#0a0a0a] p-8 md:p-10 hover:border-[#e7c15a]/40 transition-colors duration-500 group">
                  <div className="flex items-start gap-5">
                    <div className="shrink-0 h-16 w-16 md:h-20 md:w-20 grid place-items-center rounded-full border border-[#e7c15a]/50 bg-gradient-to-br from-[#1a1608] to-[#050505]">
                      <span className="font-serif-lux text-2xl md:text-3xl text-gold-gradient">{m.initials}</span>
                    </div>
                    <div>
                      <h3 className="font-serif-lux text-3xl md:text-4xl text-[#fafafa] leading-none">{m.name}</h3>
                      <div className="mt-2 text-xs uppercase tracking-[0.22em] text-[#e7c15a]">{m.role}</div>
                    </div>
                  </div>

                  <div className="mt-7 space-y-4 text-[#c5c5c5] text-sm md:text-[15px] leading-relaxed">
                    {m.bio.map((p, idx) => <p key={idx}>{p}</p>)}
                  </div>

                  {m.quote && (
                    <div className="mt-7 border-t border-white/8 pt-6 flex gap-3">
                      <Quote size={20} className="shrink-0 text-[#e7c15a]/60" strokeWidth={1.4} />
                      <p className="font-serif-lux italic text-lg md:text-xl text-[#e8e8e8] leading-snug">&ldquo;{m.quote}&rdquo;</p>
                    </div>
                  )}
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Scope of Work — Object Clause */}
      <section data-testid="scope-section" className="py-20 md:py-28">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <Reveal className="max-w-2xl">
            <Overline>Scope of Work</Overline>
            <h2 className="font-serif-lux mt-5 text-4xl md:text-5xl leading-tight tracking-tight">
              What we <span className="text-gold-gradient italic">do.</span>
            </h2>
            <p className="mt-5 text-[#b8b8b8] leading-relaxed">
              Vernika Designs LLP is chartered to deliver interiors and interior products end-to-end — across four broad pillars.
            </p>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-2 gap-px bg-white/5 border border-white/5">
            {OBJECTS.map((o, i) => (
              <Reveal key={o.title} delay={i * 0.05}>
                <div className="group bg-[#0a0a0a] p-8 md:p-10 h-full hover:bg-[#0e0e0e] transition-colors" data-testid={`object-${i}`}>
                  <span className="font-serif-lux text-4xl md:text-5xl text-[#e7c15a]/25 group-hover:text-[#e7c15a]/70 transition-colors duration-500">{`0${i + 1}`}</span>
                  <h3 className="mt-4 font-serif-lux text-2xl md:text-3xl text-[#fafafa] leading-tight">{o.title}</h3>
                  <p className="mt-4 text-[#b8b8b8] leading-relaxed text-sm md:text-[15px]">{o.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Spaces We Transform */}
      <section className="py-20 md:py-28 bg-[#080808] border-y border-white/5">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <Reveal className="max-w-2xl">
            <Overline>Spaces We Transform</Overline>
            <h2 className="font-serif-lux mt-5 text-4xl md:text-5xl leading-tight tracking-tight">
              Every room, <span className="text-gold-gradient italic">reimagined.</span>
            </h2>
            <p className="mt-5 text-[#b8b8b8] leading-relaxed">
              From a cozy pooja corner to a complete home transformation — we design, source and finish it all.
            </p>
          </Reveal>
          <div className="mt-14 grid grid-cols-2 md:grid-cols-3 gap-px bg-white/5 border border-white/5">
            {SPACES.map((s, i) => (
              <Reveal key={s.name} delay={i * 0.04}>
                <div className="group relative bg-[#050505] aspect-[4/3] overflow-hidden" data-testid={`space-${i}`}>
                  <img src={s.image} alt={s.name} loading="lazy" className="img-lux absolute inset-0 h-full w-full object-cover transition-transform duration-[1.2s] group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/30 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#e7c15a]">Space</div>
                    <div className="mt-1 font-serif-lux text-xl md:text-2xl text-[#fafafa]">{s.name}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <WhyChoose />

      {/* Customers We Serve */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <Reveal><Overline>Customers We Serve</Overline></Reveal>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5 border border-white/5">
            {CUSTOMERS.map((c, i) => (
              <Reveal key={c} delay={i * 0.04}>
                <div className="bg-[#050505] p-7 text-center hover:bg-[#0d0d0d] transition-colors" data-testid={`customer-${i}`}>
                  <span className="font-serif-lux text-lg md:text-xl text-[#e8e8e8]">{c}</span>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-14 text-center">
              <Link to="/contact?quote=1" className="inline-flex items-center gap-2 bg-[#e7c15a] px-9 py-4 text-xs uppercase tracking-[0.2em] font-medium text-[#050505]">
                Work With Us <ArrowRight size={15} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
